import React from "react";
import { ArrowRight, Play } from "lucide-react";
import DashboardImage from "../../assets/images/dashboard.png";
import { useLanguage } from "../../context/LanguageProvider";

const HeroSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="bg-gradient-to-br from-primary to-primary-dark relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12">
          <div className="md:w-1/2 space-y-8">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium backdrop-blur-sm">
              <span className="flex h-2 w-2 rounded-full bg-emerald-400 mr-2"></span>
              {t("hero_newFeatures_badge")}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              {t("hero_title_part1")}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200">
                {t("hero_title_part2")}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-white/80 max-w-lg">
              {t("hero_description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://app.fukkoni.com/register"
                className="bg-white text-primary hover:bg-blue-50 transition-colors duration-300 px-8 py-3 rounded-md font-medium shadow-lg shadow-primary-dark/20 flex items-center group"
              >
                {t("hero_button_getStarted")}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </a>

              <button className="border border-white/30 backdrop-blur-sm bg-white/10 hover:bg-white/20 transition-colors duration-300 px-8 py-3 rounded-md font-medium flex items-center">
                <Play className="mr-2 h-4 w-4 fill-white" />
                {t("hero_button_watchDemo")}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                <img
                  src="https://d1csarkz8obe9u.cloudfront.net/posterpreviews/management-business-logo-design-template-029c8183c7ec4ff641c3853170a304ed_screen.jpg"
                  alt={t("hero_trustedBy") + " User 1"}
                  className="w-8 h-8 rounded-full border-2 border-primary"
                />
                <img
                  src="https://d1csarkz8obe9u.cloudfront.net/posterpreviews/management-business-logo-design-template-029c8183c7ec4ff641c3853170a304ed_screen.jpg"
                  alt={t("hero_trustedBy") + " User 2"}
                  className="w-8 h-8 rounded-full border-2 border-primary"
                />
                <img
                  src="https://d1csarkz8obe9u.cloudfront.net/posterpreviews/management-business-logo-design-template-029c8183c7ec4ff641c3853170a304ed_screen.jpg"
                  alt={t("hero_trustedBy") + " User 3"}
                  className="w-8 h-8 rounded-full border-2 border-primary"
                />
              </div>
              <p className="text-sm text-white/70">
                {t("hero_trustedBy")}
                <span className="font-medium text-white">2,000+</span>{" "}
                {t("hero_businesses")}
              </p>
            </div>
          </div>

          <div className="md:w-1/2">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-blue-500/20 rounded-lg backdrop-blur-md border border-white/10 -z-10"></div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-purple-500/20 rounded-lg backdrop-blur-md border border-white/10 -z-10"></div>

              <div className="bg-gradient-to-tr from-white/10 to-white/5 p-2 sm:p-4 rounded-lg backdrop-blur-sm border border-white/10 shadow-2xl">
                <img
                  src={DashboardImage}
                  alt="Dashboard Preview"
                  className="rounded-lg w-full hover:scale-[1.02] transition-transform duration-500"
                />

                <div className="absolute -bottom-8 -right-4 bg-white rounded-lg p-4 shadow-xl hidden md:block">
                  <div className="flex items-center gap-4">
                    <div className="bg-emerald-100 p-3 rounded-full">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16 6L8 14L16 22"
                          stroke="#10B981"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 6L16 14L8 22"
                          stroke="#10B981"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">
                        {t("hero_stats_monthlySales")}
                      </p>{" "}
                      <p className="text-xl font-bold text-gray-900">+27.4%</p>{" "}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-primary to-transparent"></div>
    </section>
  );
};

export default HeroSection;
